import { clsx } from '@alexis/helpers/clsx';
import CheckboxItem from '@pages/FlightSearchResult/components/CheckboxItem';
import { useFlightPLS } from '@pages/FlightSearchResult/contexts/FlightPerLegSearchContext/FlightPerLegSearchContext';
import { useFlightSearchResults } from '@pages/FlightSearchResult/contexts/FlightSearchResultsContext';
import useFlightSearchFilters from '@pages/FlightSearchResult/hooks/useFlightSearchFilters';
import useFlightSearchPageParams from '@pages/FlightSearchResult/hooks/useFlightSearchPageParams';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import PillToggle from '@components/PillToggle';
import Switch from '@components/Switch';

import { CLOUDINARY_DAILY_KEY } from '@constants/cloudinary';

import { convertFilterStateToDataForTracking, mergeDuplicateNameAirlines } from '@helpers/flight';
import { cloudinaryImageTransformations } from '@helpers/imageTransformations';
import { convertFlightSearchResultFarePriceToPrice } from '@helpers/price';

import useFlightSearchResultsDatadog from '@hooks/useFlightSearchResultsDatadog';

import { getIsRtlState, getPriceOptionsState, getTranslationsState } from '@redux/selectors';

import { FlightSearchResultSearchParams } from '@wegoTypes/flights/flightSearchParams';

import styles from '../FlightFilterCategoryContent.module.scss';

type AirlinesFilterProps = {
  className: string;
  genzoTrackApplyFilterActionEvent: any;
  genzoTrackApplyFilterSearchView: () => void;
};

const AirlinesFilter: React.FC<AirlinesFilterProps> = ({
  className,
  genzoTrackApplyFilterActionEvent,
  genzoTrackApplyFilterSearchView,
}) => {
  const { passengerCount, isMultiCity } = useFlightSearchPageParams();
  const { isPerLegSearch } = useFlightPLS();

  const {
    tripsFilter,
    airlineCodesFilter,
    allianceCodesFilter,
    toggleSingleFilter,
    toggleMultiCitySingleFilter,
    activeLeg,
  } = useFlightSearchFilters();

  const { multiCityActiveLeg, airlineFilterOptions, allianceFilterOptions, exchangeRate } =
    useFlightSearchResults();

  const activeLegAirlineFilterOptions = useMemo(
    () =>
      isMultiCity
        ? mergeDuplicateNameAirlines(
            multiCityActiveLeg.leg?.airlines
              .map((airline, index) => ({
                id: index,
                name: airline?.code,
                price: airline?.price,
                airline: airline?.airline,
              }))
              .sort((a, b) => a.airline?.name?.localeCompare(b.airline?.name)) || [],
          )
        : mergeDuplicateNameAirlines(airlineFilterOptions),
    [isMultiCity, multiCityActiveLeg, airlineFilterOptions],
  );

  const [airlineAllianceViewMode, setAirlineAllianceViewMode] = useState<'airline' | 'alliance'>(
    'airline',
  );

  const translations = useSelector(getTranslationsState);
  const isRtl = useSelector(getIsRtlState);
  const priceOptions = useSelector(getPriceOptionsState);

  const { recordChangeFilterAction } = useFlightSearchResultsDatadog();

  const selectedAirlineForActiveLeg =
    airlineCodesFilter.find((item) => item.leg === activeLeg)?.values || [];

  const selectedAllianceForActiveLeg =
    allianceCodesFilter.find((item) => item.leg === activeLeg)?.values || [];

  const handleTripOptionChanged = (option: string) => {
    recordChangeFilterAction();

    const newFilter = toggleSingleFilter({
      searchParamKey: FlightSearchResultSearchParams.TripOptions,
      value: option,
    });

    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      complete_trip_same_airline: !newFilter.includes(option) ? 1 : 0,
    });
    genzoTrackApplyFilterSearchView();
  };

  const handleAllianceClick = (code: string) => {
    recordChangeFilterAction();

    const newFilter = toggleMultiCitySingleFilter({
      activeLeg: !!isPerLegSearch ? activeLeg : 0,
      value: code,
      searchParamKey: FlightSearchResultSearchParams.Alliances,
    });

    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      alliances: convertFilterStateToDataForTracking(
        newFilter,
        false,
        true,
        isPerLegSearch,
        activeLeg,
      ),
    });

    genzoTrackApplyFilterSearchView();
  };

  const handleAirlineClick = (codes: string[]) => {
    recordChangeFilterAction();

    const newFilter = toggleMultiCitySingleFilter({
      activeLeg,
      value: codes,
      searchParamKey: FlightSearchResultSearchParams.Airlines,
    });

    genzoTrackApplyFilterActionEvent('filter', 'applied', {
      airlines: convertFilterStateToDataForTracking(
        newFilter,
        false,
        true,
        isPerLegSearch,
        activeLeg,
      ),
    });

    genzoTrackApplyFilterSearchView();
  };

  const getImage = (airlineCode: string, type: 'airlines' | 'alliances') => {
    return (
      <img
        className={styles.image}
        src={cloudinaryImageTransformations(
          `https://assets.wego.com/image/upload/${CLOUDINARY_DAILY_KEY}/flights/${type}_square/${airlineCode}.png`,
          'c_fit',
          'w_100',
          'h_100',
          'q_auto',
          'f_auto',
          'fl_lossy',
        )}
      />
    );
  };

  return (
    <div className={clsx(styles.categoryContent, styles.inDropdown)}>
      {!isPerLegSearch ? (
        <div
          data-testid='same-airline-switch-container'
          className={styles.itemBoolean}
          onClick={() => handleTripOptionChanged('same_airline')}
        >
          {translations.complete_trip_airline}

          <Switch isRtl={isRtl} checked={tripsFilter.includes('same_airline')} />
        </div>
      ) : null}

      <div className={styles.tabsContainer}>
        <PillToggle
          className={styles.pillToggle}
          items={[
            {
              label: translations.airline as string,
              selected: airlineAllianceViewMode === 'airline',
              onClick: () => setAirlineAllianceViewMode('airline'),
            },
            {
              label: translations.alliance as string,
              selected: airlineAllianceViewMode === 'alliance',
              onClick: () => setAirlineAllianceViewMode('alliance'),
            },
          ]}
        />
      </div>

      <div className={styles.airlinesOptions}>
        {allianceFilterOptions.length > 0 &&
          !isMultiCity &&
          airlineAllianceViewMode === 'alliance' && (
            <>
              {allianceFilterOptions.map((alliance) => (
                <CheckboxItem
                  className={className}
                  key={alliance.name}
                  isChecked={selectedAllianceForActiveLeg.includes(alliance.name)}
                  label={(translations[alliance.name] as string) || ''}
                  exchangeRate={exchangeRate}
                  onCheckboxToggle={() => handleAllianceClick(alliance.name)}
                  Icon={getImage(alliance.name, 'alliances')}
                />
              ))}
            </>
          )}

        {(airlineAllianceViewMode === 'airline' || isMultiCity) && (
          <>
            {activeLegAirlineFilterOptions.map((airline) => (
              <CheckboxItem
                className={className}
                key={airline.name[0]}
                isChecked={airline.name.every((airlineName) =>
                  selectedAirlineForActiveLeg.includes(airlineName),
                )}
                label={airline.airline?.name ?? ''}
                exchangeRate={exchangeRate}
                price={convertFlightSearchResultFarePriceToPrice(
                  airline.price!,
                  passengerCount,
                  priceOptions.showTotalPrice,
                  true,
                )}
                onCheckboxToggle={() => handleAirlineClick(airline.name)}
                Icon={getImage(airline.name[0], 'airlines')}
              />
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default AirlinesFilter;
